import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Inbox, CalendarClock, Axe, Images, ImagePlus, Star, Newspaper,
  Settings as SettingsIcon, CalendarX, Users as UsersIcon, DatabaseBackup,
  LogOut, ExternalLink, Menu, X, Contact2, BarChart3, Bell, Search, KeyRound,
  Palette, HelpCircle, ChevronRight,
} from "lucide-react";
import { useAdmin } from "./AdminContext";
import CrudPanel from "./CrudPanel";
import {
  RequestsPanel, BookingsPanel, SettingsPanel, UsersPanel, CalendarPanel, BackupPanel,
} from "./panels";
import DashboardHome from "./DashboardHome";
import {
  CustomersPanel, NotificationsPanel, SeoPanel, ReportsPanel, ChangePasswordPanel,
  BrandingPanel, HelpCenterPanel,
} from "./morepanels";
import { T, CLIENT_BRAND } from "./theme";

const SERVICE_FIELDS = [{ key: "title", label: "Title" }, { key: "desc", label: "Description", type: "textarea" }, { key: "icon", label: "Icon", type: "icon" }, { key: "order", label: "Order", type: "number" }];
const PROJECT_FIELDS = [{ key: "title", label: "Title" }, { key: "category", label: "Category" }, { key: "before", label: "Before image", type: "image" }, { key: "after", label: "After image", type: "image" }, { key: "order", label: "Order", type: "number" }];
const GALLERY_FIELDS = [{ key: "tag", label: "Tag / Caption" }, { key: "src", label: "Image or video (mp4)", type: "image" }, { key: "poster", label: "Video poster (optional)", type: "image" }, { key: "order", label: "Order", type: "number" }];
const TESTIMONIAL_FIELDS = [{ key: "name", label: "Customer name" }, { key: "text", label: "Review", type: "textarea" }, { key: "rating", label: "Rating (1-5)", type: "number" }, { key: "date", label: "Date label" }, { key: "src", label: "Source" }, { key: "order", label: "Order", type: "number" }];
const BLOG_FIELDS = [{ key: "title", label: "Title" }, { key: "slug", label: "URL slug" }, { key: "excerpt", label: "Excerpt", type: "textarea" }, { key: "meta_description", label: "SEO meta description", type: "textarea" }, { key: "cover", label: "Cover image", type: "image" }, { key: "body", label: "Body (HTML)", type: "textarea", rows: 12 }, { key: "published", label: "Published", type: "checkbox" }, { key: "order", label: "Order", type: "number" }];

function BrandMark() {
  const [imgOk, setImgOk] = useState(true);
  if (imgOk) {
    return (
      <img
        src={CLIENT_BRAND.logo}
        alt={CLIENT_BRAND.name}
        onError={() => setImgOk(false)}
        className="h-10 w-auto"
        style={{ filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.4))" }}
      />
    );
  }
  return (
    <div className="flex items-center gap-2.5">
      <span
        className="inline-flex items-center justify-center w-10 h-10 rounded-xl font-black text-lg"
        style={{ background: `linear-gradient(135deg, ${T.gold}, ${T.goldHi})`, color: "#0A0A0C" }}
      >
        T
      </span>
      <div className="leading-tight">
        <div className="font-display font-extrabold text-base tracking-tight" style={{ color: T.text }}>
          Top of the Line
        </div>
        <div className="text-[9px] tracking-[0.22em] uppercase" style={{ color: T.gold }}>
          Client Portal
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAdmin();
  const role = user?.role;
  const isSuper = role === "super_admin";
  const isAdmin = role === "admin" || isSuper;
  const isEditor = role === "editor";
  const isClient = role === "client";
  const [open, setOpen] = useState(false);

  const ALL = useMemo(() => [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, el: <DashboardHome user={user} isClient={isClient} /> },
    { id: "requests", label: "Estimate Requests", icon: Inbox, el: <RequestsPanel isClient={isClient} /> },
    { id: "appointments", label: "Appointments", icon: CalendarClock, el: <BookingsPanel /> },
    { id: "customers", label: "Customers", icon: Contact2, el: <CustomersPanel /> },
    { id: "calendar", label: "Calendar", icon: CalendarX, el: <CalendarPanel /> },
    { id: "services", label: "Services", icon: Axe, el: <CrudPanel coll="services" title="Services" fields={SERVICE_FIELDS} labelKey="title" /> },
    { id: "projects", label: "Before / After", icon: ImagePlus, el: <CrudPanel coll="projects" title="Before / After" fields={PROJECT_FIELDS} labelKey="title" /> },
    { id: "gallery", label: "Gallery", icon: Images, el: <CrudPanel coll="gallery" title="Gallery" fields={GALLERY_FIELDS} labelKey="tag" /> },
    { id: "testimonials", label: "Testimonials", icon: Star, el: <CrudPanel coll="testimonials" title="Testimonials" fields={TESTIMONIAL_FIELDS} labelKey="name" /> },
    { id: "blog", label: "Blog", icon: Newspaper, el: <CrudPanel coll="blog" title="Blog Posts" fields={BLOG_FIELDS} labelKey="title" /> },
    { id: "reports", label: "Reports", icon: BarChart3, el: <ReportsPanel /> },
    { id: "notifications", label: "Notifications", icon: Bell, el: <NotificationsPanel /> },
    { id: "seo", label: "SEO", icon: Search, el: <SeoPanel /> },
    { id: "settings", label: "Business Info", icon: SettingsIcon, el: <SettingsPanel isClient={isClient} /> },
    { id: "branding", label: "Branding", icon: Palette, el: <BrandingPanel /> },
    { id: "users", label: "Team & Roles", icon: UsersIcon, el: <UsersPanel me={role} /> },
    { id: "backup", label: "Backup & Restore", icon: DatabaseBackup, el: <BackupPanel /> },
    { id: "changepw", label: "Change Password", icon: KeyRound, el: <ChangePasswordPanel /> },
    { id: "help", label: "Help Center", icon: HelpCircle, el: <HelpCenterPanel /> },
  ], [user, isClient, role]);

  // Client Portal tabs — mapped to your explicit permission list:
  //   view/respond to estimate requests, download estimate PDFs (Reports),
  //   view appointments & scheduled jobs, update basic company info + phone/email,
  //   upload/replace/remove photos (Gallery + Before/After), manage the gallery,
  //   add/edit/remove reviews (Testimonials), change own password.
  const CLIENT_TABS = new Set([
    "dashboard", "requests", "appointments", "customers",
    "gallery", "projects", "testimonials",
    "reports", "settings", "changepw", "help",
  ]);
  const EDITOR_HIDE = new Set(["users", "backup", "branding"]);

  let TABS = ALL;
  if (isClient) TABS = ALL.filter((t) => CLIENT_TABS.has(t.id));
  else if (isEditor) TABS = ALL.filter((t) => !EDITOR_HIDE.has(t.id));

  // Grouped nav
  const GROUPS = isClient
    ? [
        { title: "Overview", ids: ["dashboard"] },
        { title: "Requests & Jobs", ids: ["requests", "appointments", "customers"] },
        { title: "Website Content", ids: ["gallery", "projects", "testimonials"] },
        { title: "Company", ids: ["settings", "reports"] },
        { title: "Account", ids: ["changepw", "help"] },
      ]
    : [
        { title: "Overview", ids: ["dashboard", "notifications"] },
        { title: "Operations", ids: ["requests", "appointments", "customers", "calendar"] },
        { title: "Content", ids: ["services", "projects", "gallery", "testimonials", "blog"] },
        { title: "Growth", ids: ["reports", "seo"] },
        { title: "Configuration", ids: ["settings", "branding", "users", "backup"] },
        { title: "Account", ids: ["changepw", "help"] },
      ];

  const [active, setActive] = useState(TABS[0]?.id || "dashboard");
  const current = TABS.find((t) => t.id === active) || TABS[0];

  const NavButton = ({ t }) => {
    const Icon = t.icon;
    const isActive = active === t.id;
    return (
      <button
        key={t.id}
        data-testid={`tab-${t.id}`}
        onClick={() => { setActive(t.id); setOpen(false); }}
        title={t.label}
        className="group w-full flex items-center gap-3 px-4 py-2 text-sm font-medium rounded-lg transition-all"
        style={{
          color: isActive ? "#0A0A0C" : T.textDim,
          background: isActive ? `linear-gradient(135deg, ${T.gold}, ${T.goldHi})` : "transparent",
          fontWeight: isActive ? 600 : 500,
        }}
        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
      >
        <Icon size={16} strokeWidth={isActive ? 2.4 : 2} />
        <span className="truncate flex-1 text-left">{t.label}</span>
        {isActive && <ChevronRight size={14} />}
      </button>
    );
  };

  const SideContent = () => (
    <>
      <div className="px-4 py-4 border-b flex items-center justify-between" style={{ borderColor: T.border }}>
        <BrandMark />
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {GROUPS.map((g) => {
          const items = TABS.filter((t) => g.ids.includes(t.id));
          if (!items.length) return null;
          return (
            <div key={g.title}>
              <div className="px-3 pb-1.5 text-[10px] font-bold tracking-[0.18em] uppercase" style={{ color: T.textFaint }}>
                {g.title}
              </div>
              <div className="space-y-0.5">
                {items.map((t) => <NavButton key={t.id} t={t} />)}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t space-y-1" style={{ borderColor: T.border }}>
        <div className="px-3 py-2 mb-1 rounded-lg" style={{ background: T.card }}>
          <div className="text-xs font-semibold truncate" style={{ color: T.text }}>{user?.name || user?.email}</div>
          <div className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: T.gold }}>{(role || "").replace("_", " ")}</div>
        </div>
        <Link to="/" target="_blank"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors hover:bg-white/5"
          style={{ color: T.textDim }}>
          <ExternalLink size={15} /> View website
        </Link>
        <button onClick={logout} data-testid="admin-logout"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors hover:bg-white/5"
          style={{ color: T.textDim }}>
          <LogOut size={15} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ background: T.bg, color: T.text }}>
      <Helmet>
        <title>Top of the Line Tree Services LLC - Client Portal</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <aside className="hidden lg:flex w-[248px] flex-col fixed inset-y-0 border-r"
        style={{ background: T.side, borderColor: T.border }}>
        <SideContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <aside className="w-72 flex flex-col border-r" style={{ background: T.side, borderColor: T.border }}>
            <SideContent />
          </aside>
          <div className="flex-1" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setOpen(false)} />
        </div>
      )}

      <main className="flex-1 lg:ml-[248px] min-w-0">
        <header className="lg:hidden flex items-center justify-between px-4 h-14 sticky top-0 z-40 border-b"
          style={{ background: T.side, borderColor: T.border }}>
          <BrandMark />
          <button onClick={() => setOpen(!open)} data-testid="admin-mobile-menu" style={{ color: T.gold }}>
            {open ? <X /> : <Menu />}
          </button>
        </header>
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
          {current?.el}
        </div>
      </main>
    </div>
  );
}
